<template lang="pug">
  server-data(
    ref="nominations"
    v-model="nominations"
    :method="getEventNominations"
    :serialize-data-items="serializeNominationsItems"
    v-slot="{ loading: isLoaded, loadMoreItems }"
  )
    server-data(
      ref="availables"
      :method="getAvailableNominations"
      :no-init="!showAvailables"
      v-model="availables"
      v-slot="{ loading: isLoadedAvailables }"
    )
      //- p.mb-1 Selected: {{ selected }}
      //- p.mb-1 Treeview: {{ treeview }}
      ev-autocomplete(
        ref="autocomplete"
        v-model="selected"
        :loading="isLoaded || isLoadedAvailables"
        :items="autocompleteItems"
        v-bind="extendAttrs"
        v-on="$listeners"
        @load-more-items="loadMoreItems"
      )
        template(
          v-if="multiple && autocompleteItems.length"
          v-slot:prepend-item
        )
          .sticky.redesignControlsBackground.z-index-10
            app-button.rounded-12.no-before.full-width(
              color="redesignPrimary"
              text
              full-width
              @click="selectAll"
            )
              span.weight-400.body-2.mr-auto Выбрать всё

        template(v-slot:selection="{ item, index }")
          span {{ item[itemText] }}
          template(v-if="hasSelectionNestedItems(item)")
            span.pl-1 ({{ getNestedItemNames(item) }})
          template(v-if="Array.isArray(selected) && selected.length - 1 !== index")
            span , &nbsp;

        template(v-slot:item="{ item, on, attrs }")
          template(v-if="canShowNested")
            v-list-item.px-0(
              :ripple="false"
              @mousedown="on.mousedown"
            )
              v-treeview.full-width(
                v-bind="extendNestedAttrs"
                :value="treeview[item.id]"
                :active="treeview[item.id]"
                :open.sync="treeviewOpen[item.id]"
                :disabled="item.disabled"
                :selectable="multiple"
                :items="[item]"
                open-on-click
                activatable
                @input="onSelect($event, item, attrs)"
                @update:active="onActive($event, item, attrs)"
                @update:open="updateDimensions"
              )
                template(v-slot:label="{ item }")
                  div.py-2.d-flex.flex-column
                    span {{ item[extendNestedAttrs.itemText] }}
                    template(v-if="item.disabled")
                      span.caption.redesignError--text
                        | {{ DEFAULT_REJECT_MESSAGE }}

          template(v-else)
            v-list-item(
              v-bind="attrs"
              v-on="on"
            )
              template(v-if="multiple")
                v-list-item-action
                  v-checkbox(
                    :inputValue="attrs.inputValue"
                    :disabled="attrs.disabled"
                  )
              v-list-item-content
                v-list-item-title {{ item[itemText] }}
                template(v-if="item.disabled")
                  v-list-item-subtitle.caption.redesignError--text
                    | {{ DEFAULT_REJECT_MESSAGE }}
</template>

<script>
/** 28.02.25 @refactor (по возможности, исправить):
 * #Лор: Столкнулся с проблемой, когда у нас 1 номинация (default), парочка групп, включен Autoselect:
 * Запускается бесконечный цикл OnSelect, выбираются все группы (а нужно 1), и страница виснет намертво.
 * #Временное_решение: Оставил парочку костылей, все подписал под @kostyl(_1, _2)
 */
// Замена старому SelectNominations.vue
// TODO: вынести базовую логику в EvAutocompleteTreeview
import isEqual from 'lodash/isEqual';
import Nomination from '@/models/list/nomination';
import { DEFAULT_NOMINATION } from '@/modules/Events/constants';
import { PER_PAGE } from '@/constants/pagination';

const DEFAULT_NESTED_PROPS = {
  itemValue: 'id',
  itemText: 'name',
  itemChildren: 'groups',
  returnObject: false,
  outputKey: 'selected_groups',
  multipleActive: false,

  get itemKey() {
    return this.itemValue;
  },
};

const DEFAULT_AUTOSELECT_PROPS = {
  whenHasOnlyDefault: false,
  whenHasNotManyGroups: false,
};

const DEFAULT_REJECT_MESSAGE = 'Не подходите по условиям';

const AUTOSELECT_INDEX_ITEM = 0;

export default {
  name: 'SelectNominations',
  props: {
    event: {
      type: Object,
      required: true,
    },
    // Replace old filters prop
    serializeData: {
      type: Function,
      default: (data) => data,
    },
    // Replace old selectFirst prop
    autoselect: {
      type: Boolean,
      default: false,
    },
    // Replace old selectFirstWhenEmpty prop
    autoselectProps: {
      type: Object,
      default: () => ({}),
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    returnObject: {
      type: Boolean,
      default: false,
    },
    showNested: {
      type: Boolean,
      default: false,
    },
    showAvailables: {
      type: Boolean,
      default: false,
    },
    nestedProps: {
      type: Object,
      default: () => ({}),
    },
    itemValue: {
      type: String,
      default: 'id',
    },
    itemText: {
      type: String,
      default: 'name',
    },
    closeAfterSelect: {
      type: Boolean,
      default: false,
    },
    query: {
      type: Object,
      default: () => ({}),
    },
    value: {
      type: [Object, String, Number, Array],
      default: undefined,
    },
  },
  data() {
    return {
      selected: this.value,
      treeview: {},
      treeviewOpen: {},
      nominations: [],
      availables: [],

      DEFAULT_REJECT_MESSAGE,

      hasAutoselected: false,
    };
  },
  computed: {
    showDefaultNomination() {
      const { nominations, event } = this;
      const hasNominations = event?.options?.has_nominations ?? true;
      return nominations.length === 1 && !hasNominations;
    },
    cleanedNominations() {
      const { nominations, showDefaultNomination } = this;

      if (showDefaultNomination) {
        return nominations;
      }

      return nominations.filter((nomination) => nomination.name !== DEFAULT_NOMINATION.NAME);
    },
    serializedNominations() {
      const { serializeData, cleanedNominations } = this;
      return serializeData(cleanedNominations);
    },
    autocompleteItems() {
      const { serializedNominations, showAvailables, availables } = this;

      return serializedNominations.map((item) => {
        const availableItem = availables.find((i) => i.id === item.id);
        const isAvailable = !!availableItem || !showAvailables;
        const { actualGroups, groups } = new Nomination(availableItem || {});
        return {
          ...item,
          disabled: !isAvailable,
          groups: isAvailable ? actualGroups || groups || [] : item?.actualGroups || item?.groups || [],
        };
      });
    },
    notDisabledItems() {
      return [...this.autocompleteItems || []].filter((i) => !i.disabled);
    },
    canShowNested() {
      return this.showNested && this.returnObject;
    },
    extendAutoselectProps() {
      const { autoselectProps } = this;
      return {
        ...DEFAULT_AUTOSELECT_PROPS,
        ...autoselectProps,
      };
    },
    extendNestedAttrs() {
      const { nestedProps } = this;
      return {
        ...DEFAULT_NESTED_PROPS,
        ...nestedProps,
      };
    },
    extendAttrs() {
      const { $attrs, $props } = this;
      const { itemText, itemValue, multiple, returnObject } = $props;

      return {
        itemText,
        itemValue,
        ...$attrs,
        multiple,
        returnObject,
      };
    },
  },
  watch: {
    value: {
      handler(val) {
        this.selected = val;

        const items = [].concat(val || []);
        const { outputKey } = this.extendNestedAttrs;

        this.treeview =
          items.reduce((acc, item) => {
            if (!item?.id) return acc;
            const output = item?.[outputKey] || this.treeview[item.id];
            return { ...acc, [item.id]: output };
          }, {}) || {};
      },
      immediate: true,
    },
    selected: {
      handler(val) {
        if (this.closeAfterSelect) {
          this.$refs.autocomplete.blur();

          if (!val) this.treeviewOpen = {};
        }

        this.$emit('input', val);
      },
      deep: true,
    },
    notDisabledItems() {
      if (this.notDisabledItems?.length) this.onAutoselect();
    },
  },
  methods: {
    getEventNominations() {
      const { event, query } = this;

      const params = {
        'search[event_id]': event.id,
        'per-page': PER_PAGE.infinity,
        expand: 'groups',
        sort: 'id',
        ...query,
      };

      return this.$rApi.eventNominations.getAll({ params });
    },
    serializeNominationsItems(data) {
      return data.map((nomination) => new Nomination(nomination));
    },
    getAvailableNominations() {
      const { event } = this;
      const { id } = event;

      const params = {
        'per-page': PER_PAGE.infinity,
        'search[event_id]': id,
        expand: 'groups',
      };
      const args = [id];

      return this.$rApi.events.getEventAvailableNominations({ args, params });
    },
    updateDimensions() {
      this.$refs.autocomplete.updateMenuDimensions();
    },
    initNominations() {
      this.$refs.nominations.init();
    },
    getOutputValue(items, props = this.extendAttrs) {
      const { multiple, itemValue, returnObject } = props;
      const lastPos = -1;
      let output = [...(items || [])];

      if (!returnObject) {
        output = output.map((i) => i[itemValue] || i);
      }

      return multiple ? output : output.at(lastPos);
    },
    onSelect(groups, item) {
      const { selected, hasNestedItems } = this;
      const { outputKey } = this.extendNestedAttrs;

      this.$set(this.treeview, item.id, groups);

      const items = [].concat(selected || []);
      const itemIndex = items.findIndex((i) => i.id === item.id);

      if (groups.length) {
        const outputGroups = hasNestedItems(item) ? groups : null;
        if (itemIndex === -1) items.push({ ...item, [outputKey]: outputGroups });
        else items[itemIndex][outputKey] = outputGroups;

        this.selected = this.getOutputValue(items);
        return;
      }

      if (itemIndex === -1) {
        this.$delete(this.treeview, item.id);
        return;
      }

      items.splice(itemIndex, 1);
      this.selected = this.getOutputValue(items);
    },
    onActive(groups, item) {
      const target = this.treeview[item.id];

      if (isEqual(groups, target)) return;

      this.updateDimensions();
      this.onSelect(groups, item);
    },
    onAutoselect() {
      if (this.cannotAutoselect() || this.hasAutoselected) return;

      this.select(AUTOSELECT_INDEX_ITEM);
      this.hasAutoselected = true; // @kostyl_1, Остановка бесконечного цикла
    },
    cannotAutoselect() {
      const { autoselect, showDefaultNomination, extendNestedAttrs } = this;
      if (!autoselect) return true;

      const conditions = [];

      const { whenHasOnlyDefault, whenHasNotManyGroups } = this.extendAutoselectProps;

      if (whenHasOnlyDefault) conditions.push(showDefaultNomination);
      if (whenHasNotManyGroups) {
        const selectTarget = this.getTargetsForSelect(AUTOSELECT_INDEX_ITEM).at(0);
        const groups = selectTarget[extendNestedAttrs.itemChildren];
        conditions.push(groups.length <= 1);
      }
      if (!conditions.length) return false;
      return !conditions.every(Boolean);
    },
    getTargetsForSelect(index = null) {
      const items = [...(this.notDisabledItems || [])];
      return index === null ? [...items] : [].concat(items[index]);
    },
    select(index = null) {
      const { showNested, getOutputValue } = this;
      const target = this.getTargetsForSelect(index);

      if (showNested && target.length) {
        const { extendNestedAttrs } = this;

        target.forEach((item) => {
          const groups = item[extendNestedAttrs.itemChildren];
          const filteredGroups = // @kostyl_2, выбор только 1 группы (оно автоматически происходит, но не во время autoselect)
            !this.cannotAutoselect() && !this.hasAutoselected
              ? [(groups || []).at(0)]
              : groups;
          const output = filteredGroups.length ? filteredGroups : getOutputValue([item]);
          this.onSelect(output, item);
        });
        return; // из-за того, что выше внутри ForEach у нас "onSelect", идущий ниже "getOutputValue" перекрывал изменения и не давал сохранить группу
      }
      this.selected = this.getOutputValue(target);
    },
    hasNestedItems(item) {
      const { extendNestedAttrs } = this;
      return !!item[extendNestedAttrs.itemChildren]?.length;
    },
    hasSelectionNestedItems(item) {
      const { hasNestedItems, treeview } = this;
      return hasNestedItems(item) && !!treeview?.[item.id]?.length;
    },
    getNestedItemNames(item) {
      const { treeview, extendNestedAttrs } = this;
      const groups = item[extendNestedAttrs.itemChildren] || [];
      const target = treeview[item.id];

      const list = target.map((i) => {
        const group = groups.find((g) => g.id === (i.id || i));
        return group?.name || `Группа №${group.id}`;
      });
      return list.join(', ');
    },
    selectAll() {
      this.select();
    },
  },
};
</script>
